import React, { useState, useEffect, useRef } from "react";
import { ethers } from "ethers";
import {
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Paper,
    IconButton,
    Tooltip,
    Chip,
    useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningIcon from "@mui/icons-material/Warning";
import { generateRandomSalt } from "../../utils/saltGenerator";
import { useWallet } from "../context/WagmiWalletContext";
import backendDataHandler from "../../services/backendDataHandler";
import AddressSelector from "../common/AddressSelector";
import workflowStorageService from "../../services/workflowStorageService";
import { useContracts } from "../context/ContractContext";
import logger from '../../utils/logger';
import { encryptForSession } from '../../utils/sessionEncryption';
import { generateSecureReference } from '../../utils/clientEncryption';
import WeiInputConverter from "../utils/WeiInputConverter";

interface FunctionBuilderProps {
    abi: Array<{
        name?: string;
        type: string;
        inputs?: Array<{ name: string; type: string }>;
        stateMutability?: 'view' | 'pure' | 'nonpayable' | 'payable';
    }>;
    onFunctionBuilt: (encodedData: string) => void;
    parentFunctionName?: string;
    onFunctionSelect: (functionName: string) => void;
    inputs: { [key: string]: string[] };
    onInputChange: (functionName: string, value: string, index: number) => void;
    selectedFunction?: string;
    contractAddress?: string;
    isWorkflowStep?: boolean; // New prop to indicate this is part of a workflow
}

interface Template {
    contract: string;
    functionName: string;
    inputs: string[];
}

const saveTemplate = (template: { contract: string; functionName: string; inputs: any[] }) => {
    logger.log("Template saved:", template);
};

const FunctionBuilder: React.FC<FunctionBuilderProps> = ({
    abi,
    onFunctionBuilt,
    parentFunctionName,
    onFunctionSelect,
    inputs,
    onInputChange,
    selectedFunction: parentSelectedFunction,
    contractAddress,
    isWorkflowStep = false,
}) => {
    const walletContext = useWallet();
    const provider = walletContext.provider;
    const { contracts, protocolAddresses } = useContracts();
    const theme = useTheme();

    const [encodedData, setEncodedData] = useState<string | null>(null);
    const [selectedFunctionObj, setSelectedFunctionObj] = useState<any | null>(null);
    const [localInputs, setLocalInputs] = useState<{ [key: string]: string }>({});
    const [templates, setTemplates] = useState<Template[]>([]);
    const [selectedTemplateIndex, setSelectedTemplateIndex] = useState<number | "">("");
    const [isStoringSecurely, setIsStoringSecurely] = useState(false);
    const [backendStorageSuccess, setBackendStorageSuccess] = useState(false);
    const [workflowReference, setWorkflowReference] = useState<string | null>(null);

    const prevAbiRef = useRef<string>("");
    const renderCountRef = useRef(0);
    renderCountRef.current += 1;

    logger.log(`FunctionBuilder render #${renderCountRef.current} - Parent function: "${parentSelectedFunction}", Selected obj: ${selectedFunctionObj?.name || 'none'}`);

    const loadTemplates = async () => {
        try {
            // Try to load from backend first
            const response = await workflowStorageService.listTemplates();
            if (response.success && response.data && Array.isArray(response.data)) {
                // Check if there are any templates to retrieve
                if (response.data.length === 0) {
                    logger.log('📥 No templates found in backend, using empty array');
                    setTemplates([]);
                    return;
                }

                logger.log('📥 Found template references in backend:', response.data.length);

                // The listTemplates returns an array of {id, timestamp, name}, so we need to fetch each template
                // But we'll do this more defensively and stop on first few failures
                const templatePromises = response.data.slice(0, 5).map(async (templateRef: any) => {
                    try {
                        const templateResponse = await workflowStorageService.retrieveTemplate(templateRef.id);
                        if (templateResponse.success && templateResponse.data) {
                            return {
                                contract: templateResponse.data.contract || 'Unknown',
                                functionName: templateResponse.data.functionName || 'Unknown',
                                inputs: templateResponse.data.inputs || []
                            };
                        }
                        logger.warn('Template retrieval failed for:', templateRef.id, 'but continuing...');
                        return null;
                    } catch (error) {
                        logger.warn('Failed to retrieve template:', templateRef.id, 'Error:', error instanceof Error ? error.message : error);
                        return null;
                    }
                });

                const backendTemplates = (await Promise.all(templatePromises)).filter((template): template is Template => template !== null);
                logger.log('📥 Successfully loaded templates from backend:', backendTemplates.length, 'out of', response.data.length, 'references');

                // If we got no valid templates but there were references, there's likely a database issue
                if (backendTemplates.length === 0 && response.data.length > 0) {
                    logger.warn('⚠️ Database contains', response.data.length, 'template references but none could be retrieved. This suggests corrupted data.');
                    logger.warn('💡 Consider running database cleanup to remove orphaned template references.');
                }

                setTemplates(backendTemplates);
                return;
            }
        } catch (error) {
            logger.warn('Backend template loading failed, falling back to localStorage:', error instanceof Error ? error.message : error);
        }

        // Fallback to localStorage with better error handling
        try {
            const localStorageData = localStorage.getItem("transactionTemplates");
            if (!localStorageData) {
                logger.log('📥 No localStorage templates found, using empty array');
                setTemplates([]);
                return;
            }

            // Validate that the data is actually JSON before parsing
            if (!localStorageData.trim().startsWith('[') && !localStorageData.trim().startsWith('{')) {
                logger.warn('📥 localStorage data is not valid JSON, clearing and using empty array');
                localStorage.removeItem("transactionTemplates");
                setTemplates([]);
                return;
            }

            const savedTemplates = JSON.parse(localStorageData);
            if (!Array.isArray(savedTemplates)) {
                logger.warn('📥 localStorage templates is not an array, using empty array');
                setTemplates([]);
                return;
            }

            logger.log('📥 Loaded templates from localStorage fallback:', savedTemplates.length);
            setTemplates(savedTemplates);
        } catch (error) {
            logger.error('Failed to load templates from localStorage, clearing corrupted data:', error);
            // Clear corrupted localStorage data
            localStorage.removeItem("transactionTemplates");
            setTemplates([]);
        }
    };

    useEffect(() => {
        // Skip template loading for workflow steps
        if (isWorkflowStep) {
            logger.log('🔄 Skipping template loading - this is a workflow step');
            setTemplates([]);
            return;
        }

        loadTemplates();
    }, [isWorkflowStep]);

    useEffect(() => {
        const abiFingerprint = JSON.stringify(abi.map(item => item.name));
        if (prevAbiRef.current !== abiFingerprint) {
            logger.log("ACTUAL ABI CHANGE DETECTED - resetting function selection");
            prevAbiRef.current = abiFingerprint;
            setSelectedFunctionObj(null);
            onFunctionSelect('');
        }
    }, [abi]);

    useEffect(() => {
        if (!parentSelectedFunction || parentSelectedFunction === '') {
            return;
        }

        if (selectedFunctionObj && selectedFunctionObj.name === parentSelectedFunction) {
            // Function is already selected, but check if we need to update inputs from parent
            if (inputs[parentSelectedFunction] && Array.isArray(inputs[parentSelectedFunction])) {
                const prefilledInputs: { [key: string]: string } = {};
                selectedFunctionObj.inputs?.forEach((input: any, index: number) => {
                    const prefilledValue = inputs[parentSelectedFunction][index];
                    if (prefilledValue !== undefined && prefilledValue !== '') {
                        prefilledInputs[input.name || ''] = prefilledValue;
                        logger.log(`🔧 Pre-filling input "${input.name}" with value:`, prefilledValue);
                    } else {
                        prefilledInputs[input.name || ''] = localInputs[input.name || ''] || "";
                    }
                });
                setLocalInputs(prefilledInputs);
                logger.log('✅ Updated localInputs with pre-filled values:', prefilledInputs);
            }
            return;
        }

        const funcObj = abi.find((item) =>
            item.type === "function" && item.name === parentSelectedFunction
        );

        if (funcObj) {
            const initialInputs: { [key: string]: string } = {};

            // CRITICAL FIX: Check for workflow auto-selection data with resolvedInputs
            let workflowResolvedInputs: any = null;
            try {
                const autoSelectionData = sessionStorage.getItem('workflowAutoSelection');
                if (autoSelectionData) {
                    const parsedData = JSON.parse(autoSelectionData);
                    if (parsedData.resolvedInputs && parsedData.functionName === parentSelectedFunction) {
                        workflowResolvedInputs = parsedData.resolvedInputs;
                        logger.log('🎯 Found workflow resolvedInputs for auto-population:', workflowResolvedInputs);
                    }
                }
            } catch (error) {
                logger.warn('Failed to parse workflow auto-selection data:', error);
            }

            if (funcObj.inputs && Array.isArray(funcObj.inputs)) {
                funcObj.inputs.forEach((input, index) => {
                    let inputValue = "";

                    // Priority 1: Workflow resolved inputs (from WorkflowExecutor)
                    if (workflowResolvedInputs && workflowResolvedInputs[input.name]) {
                        inputValue = workflowResolvedInputs[input.name];
                        logger.log(`🎯 Using workflow resolved input for "${input.name}":`, inputValue);
                    }
                    // Priority 2: Parent pre-filled inputs (from TransactionBuilder)
                    else if (inputs[parentSelectedFunction]?.[index] !== undefined && inputs[parentSelectedFunction][index] !== '') {
                        inputValue = inputs[parentSelectedFunction][index];
                        logger.log(`🎯 Using parent pre-filled input for "${input.name}":`, inputValue);
                    }
                    // Priority 3: Map common parameter names for timelock.execute
                    else if (workflowResolvedInputs && parentSelectedFunction === 'execute') {
                        // Map timelock.execute parameter names to schedule data
                        const parameterMap: { [key: string]: string } = {
                            'target': 'target',
                            'value': 'value',
                            'payload': 'data',  // timelock.execute uses 'payload' for what schedule calls 'data'
                            'data': 'data',     // some contracts might use 'data' directly
                            'predecessor': 'predecessor',
                            'salt': 'salt'
                        };

                        const mappedKey = parameterMap[input.name];
                        if (mappedKey && workflowResolvedInputs[mappedKey]) {
                            inputValue = workflowResolvedInputs[mappedKey];
                            logger.log(`🎯 Mapped workflow input "${mappedKey}" to parameter "${input.name}":`, inputValue);
                        }
                    }

                    initialInputs[input.name || ''] = inputValue;
                });
            }

            setLocalInputs(initialInputs);
            setSelectedFunctionObj(funcObj);
            logger.log('🔧 Function selected with workflow auto-filled inputs:', parentSelectedFunction, initialInputs);
        }
    }, [parentSelectedFunction, abi, inputs]);

    const handleTemplateSelect = (index: number | "") => {
        setSelectedTemplateIndex(index);
        if (index === "") return;
        const template = templates[index as number];
        if (template) {
            const functionFromTemplate = abi.find((item) => item.name === template.functionName);
            if (functionFromTemplate) {
                setSelectedFunctionObj(functionFromTemplate);
                if (functionFromTemplate.name) {
                    onFunctionSelect(functionFromTemplate.name);
                }
                const newLocalInputs: { [key: string]: string } = {};
                functionFromTemplate.inputs?.forEach((input: any, idx: number) => {
                    newLocalInputs[input.name] = template.inputs[idx] || "";
                });
                setLocalInputs(newLocalInputs);
            }
        }
    };

    const handleFunctionSelect = (func: any) => {
        logger.log("Function selected:", func.name);
        setSelectedFunctionObj(func);

        // Initialize inputs with pre-filled values if available
        const initialInputs: { [key: string]: string } = {};
        if (func.inputs && Array.isArray(func.inputs)) {
            func.inputs.forEach((input: any, index: number) => {
                const prefilledValue = inputs[func.name]?.[index];
                if (prefilledValue !== undefined && prefilledValue !== '') {
                    initialInputs[input.name || ''] = prefilledValue;
                    logger.log(`🎯 Pre-filling input "${input.name}" on function select:`, prefilledValue);
                } else {
                    initialInputs[input.name || ''] = "";
                }
            });
        }
        setLocalInputs(initialInputs);
        onFunctionSelect(func.name);
    };

    const handleInputChange = (name: string, value: string) => {
        setLocalInputs((prev) => ({ ...prev, [name]: value }));
        const idx = selectedFunctionObj?.inputs?.findIndex((i: any) => i.name === name) ?? 0;
        onInputChange(selectedFunctionObj?.name || "", value, idx);
    };

    const handleBuildFunction = async () => {
        if (selectedFunctionObj) {
            setIsStoringSecurely(true);

            try {
                const iface = new ethers.utils.Interface(abi);

                const params = await Promise.all(
                    selectedFunctionObj.inputs?.map(async (input: any) => {
                        // Get the input value, handling both string and array types
                        const inputValue = inputs[selectedFunctionObj.name]?.[input.name] || inputs[selectedFunctionObj.name];
                        const arrayValue = Array.isArray(inputValue) ? inputValue[0] : inputValue;
                        let val: string = localInputs[input.name] || arrayValue || "";

                        logger.log(`🔍 Processing input "${input.name}":`, {
                            localInputs: localInputs[input.name] ? `${typeof localInputs[input.name]} (${localInputs[input.name]?.substring(0, 30)}...)` : 'undefined',
                            arrayValue: arrayValue ? `${typeof arrayValue} (${arrayValue?.substring(0, 30)}...)` : 'undefined',
                            finalVal: val ? `${typeof val} (${val?.substring(0, 30)}...)` : 'undefined'
                        });

                        // CRITICAL FIX: Check if val is a JSON object string and extract functionData
                        if (val && typeof val === 'string' && val.startsWith('{') && val.includes('functionData')) {
                            try {
                                const jsonObj = JSON.parse(val);
                                if (jsonObj.functionData) {
                                    logger.log(`🔧 EXTRACTING functionData from JSON object for input "${input.name}"`);
                                    logger.log(`🔧 Original JSON length: ${val.length}, extracting hex: ${jsonObj.functionData.substring(0, 30)}...`);
                                    val = jsonObj.functionData;
                                }
                            } catch (error) {
                                logger.warn(`Failed to parse JSON object for input "${input.name}":`, error);
                            }
                        }

                        logger.log(`🔍 After JSON extraction - Final value:`, typeof val, val?.substring(0, 50) + "...");

                        // WORKFLOW FIX: Handle KV references for timelock functions like schedule/execute
                        // Check if this is a timelock workflow step that should pass KV references through
                        const isTimelockWorkflowStep = selectedFunctionObj.name === 'schedule' || selectedFunctionObj.name === 'execute';
                        const isDataParameter = input.name === 'data' || input.name === 'payload';

                        // Check if this is a secure reference and auto-decrypt if needed
                        // Handle cases where the reference might have been prefixed with 0x
                        let cleanVal = val;
                        let hasHexPrefix = false;

                        logger.log(`🔍 Checking for secure reference - Input "${input.name}":`, {
                            val: val?.substring(0, 50) + '...',
                            startsWithHex: val?.startsWith('0x{{'),
                            endsWithBraces: val?.endsWith('}}'),
                            isString: typeof val === 'string',
                            isTimelockWorkflowStep,
                            isDataParameter
                        });

                        if (val && typeof val === 'string' && val.startsWith('0x{{') && val.endsWith('}}')) {
                            cleanVal = val.substring(2); // Remove 0x prefix
                            hasHexPrefix = true;
                            logger.log(`🔧 Detected 0x-prefixed secure reference, cleaning: ${val} → ${cleanVal}`);
                        }

                        // Also check for clean references without 0x prefix
                        const isSecureRef = backendDataHandler.isSecureReference(cleanVal);
                        logger.log(`🔍 Secure reference check result:`, {
                            cleanVal: cleanVal?.substring(0, 50) + '...',
                            isSecureRef,
                            hasHexPrefix
                        });

                        // CRITICAL FIX: Both timelock.schedule AND timelock.execute need decoded hex data, not KV references
                        // The KV reference contains the original Memer function hex data that must be decoded
                        if (cleanVal && typeof cleanVal === 'string' && isSecureRef) {
                            try {
                                logger.log('🔄 Auto-retrieving data from backend:', cleanVal);
                                const result = await backendDataHandler.autoDecryptIfNeeded(cleanVal);
                                if (result.success && result.data) {
                                    // Check if retrieved data is a function metadata object
                                    if (typeof result.data === 'object' && result.data !== null && 'functionData' in result.data) {
                                        // Extract just the hex string from the metadata object
                                        val = (result.data as any).functionData;
                                        logger.log("✅ Auto-retrieved input value for", input.name, "- Extracted functionData hex");
                                        logger.log("🔍 Extracted hex data:", val.substring(0, 50) + "...");
                                    } else if (typeof result.data === 'string') {
                                        // Check if the string data is actually a JSON object
                                        if (result.data.startsWith('{') && result.data.includes('functionData')) {
                                            try {
                                                const jsonObj = JSON.parse(result.data);
                                                if (jsonObj.functionData) {
                                                    val = jsonObj.functionData;
                                                    logger.log("✅ Auto-retrieved input value for", input.name, "- Extracted functionData from JSON string");
                                                    logger.log("🔍 Extracted hex data:", val.substring(0, 50) + "...");
                                                } else {
                                                    val = result.data;
                                                    logger.log("✅ Auto-retrieved input value for", input.name, "- Direct string data");
                                                }
                                            } catch (error) {
                                                val = result.data;
                                                logger.log("✅ Auto-retrieved input value for", input.name, "- Direct string data (JSON parse failed)");
                                            }
                                        } else {
                                            // Use the string data directly (already hex or other string data)
                                            val = result.data;
                                            logger.log("✅ Auto-retrieved input value for", input.name, "- Direct string data");
                                            logger.log("🔍 Retrieved data preview:", val.substring(0, 50) + "...");
                                        }
                                    } else {
                                        // For other data types, convert to string
                                        val = String(result.data);
                                        logger.log("✅ Auto-retrieved input value for", input.name, "- Converted to string");
                                    }

                                    logger.log(" Final parameter value - Type:", typeof val, "Length:", val.length);

                                    // CRITICAL FIX: Compute operationId for timelock.schedule function when we have decrypted data
                                    if (selectedFunctionObj.name === 'schedule' && isDataParameter && val && typeof val === 'string') {
                                        try {
                                            logger.log('🎯 OPERATIONID FIX: Computing operationId with resolved parameters...');

                                            // Import computeOperationId function
                                            const { computeOperationId } = await import('../../utils/timelockDecoder');

                                            // CRITICAL FIX: Resolve ALL parameters that might contain KV references
                                            const resolveParameter = async (paramName: string, defaultValue: string) => {
                                                const rawValue = localInputs[paramName] || defaultValue;

                                                // Check if this parameter is also a KV reference
                                                if (rawValue && typeof rawValue === 'string' && backendDataHandler.isSecureReference(rawValue)) {
                                                    try {
                                                        const result = await backendDataHandler.autoDecryptIfNeeded(rawValue);
                                                        if (result.success && result.data) {
                                                            // Extract the actual value from the result
                                                            if (typeof result.data === 'object' && result.data !== null && 'functionData' in result.data) {
                                                                return (result.data as any).functionData;
                                                            } else if (typeof result.data === 'string') {
                                                                return result.data;
                                                            }
                                                        }
                                                        logger.warn(`⚠️ Failed to resolve KV reference for parameter ${paramName}`);
                                                        return rawValue; // Fallback to original value
                                                    } catch (error) {
                                                        logger.warn(`⚠️ Error resolving KV reference for parameter ${paramName}:`, error instanceof Error ? error.message : 'Unknown error');
                                                        return rawValue; // Fallback to original value
                                                    }
                                                }
                                                return rawValue;
                                            };

                                            // Resolve all schedule parameters
                                            const target = await resolveParameter('target', '');
                                            const value = await resolveParameter('value', '0');
                                            const predecessor = await resolveParameter('predecessor', '0x0000000000000000000000000000000000000000000000000000000000000000');
                                            const salt = await resolveParameter('salt', '');

                                            if (target && salt) {
                                                const computedOperationId = computeOperationId(
                                                    target,      // resolved target address
                                                    value,       // resolved value
                                                    val,         // resolved data (decrypted Memer function hex)
                                                    predecessor, // resolved predecessor
                                                    salt         // resolved salt
                                                );

                                                logger.log('✅ OPERATIONID FIX: Successfully computed operationId');

                                                // Store the computed operationId in sessionStorage for the workflow completion service
                                                sessionStorage.setItem('computedOperationId', computedOperationId);
                                                logger.log('💾 OPERATIONID FIX: Stored operationId in sessionStorage for workflow completion');

                                                // Store a flag indicating successful resolution (without storing sensitive data)
                                                sessionStorage.setItem('parametersResolved', 'true');
                                                logger.log('💾 OPERATIONID FIX: Marked parameters as successfully resolved');

                                            } else {
                                                logger.warn('⚠️ OPERATIONID FIX: Missing required parameters for operationId computation:', {
                                                    target: target ? 'present' : 'missing',
                                                    salt: salt ? 'present' : 'missing'
                                                });
                                            }

                                        } catch (operationIdError) {
                                            logger.error('❌ OPERATIONID FIX: Failed to compute operationId in FunctionBuilder:', operationIdError);
                                            // Don't fail the function building if operationId computation fails
                                        }
                                    }

                                } else {
                                    logger.error("❌ Failed to auto-retrieve input:", result.error);
                                    // Don't use the reference string as data - throw an error instead
                                    throw new Error(`Failed to retrieve workflow data: ${result.error}`);
                                }
                            } catch (error) {
                                logger.error("❌ Failed to auto-retrieve input:", error);
                                // Don't use the reference string as data - throw an error instead
                                throw new Error(`Backend retrieval failed: ${error instanceof Error ? error.message : String(error)}`);
                            }
                        }

                        if (!val || val === "") {
                            if (input.type === "address") return "0x0000000000000000000000000000000000000000";
                            if (input.type.startsWith("uint")) return "0";
                            if (input.type === "bytes32") return "0x0000000000000000000000000000000000000000000000000000000000000000";
                            if (input.type.startsWith("bytes")) return "0x";
                            return "";
                        }

                        // SAFETY CHECK: Prevent double 0x prefix FIRST
                        if (val && typeof val === 'string' && val.startsWith('0x0x')) {
                            val = val.substring(2); // Remove the first 0x
                            logger.log(`🔧 Fixed double 0x prefix for parameter "${input.name}":`, val.substring(0, 30) + '...');
                        }

                        // CRITICAL FIX: For bytes parameters, ensure hex data is properly formatted
                        if (input.type.startsWith("bytes") && val && typeof val === 'string') {
                            // If it's already valid hex data, use it directly
                            if (val.startsWith('0x') && val.length > 2) {
                                logger.log(`✅ Using hex data directly for bytes parameter "${input.name}":`, val.substring(0, 30) + '...');
                                return val;
                            }
                            // If it's not hex, ensure it starts with 0x (but avoid double 0x prefix)
                            if (!val.startsWith('0x')) {
                                val = '0x' + val;
                                logger.log(`🔧 Added 0x prefix to bytes parameter "${input.name}":`, val.substring(0, 30) + '...');
                            }
                        }

                        return val;
                    }) || []
                );

                const data = iface.encodeFunctionData(selectedFunctionObj.name, params);
                setEncodedData(data);

                // SECURITY FIX: Encrypt sensitive data before sending to backend
                try {
                    // Generate secure reference ID
                    const functionId = generateSecureReference();

                    // Create function metadata (non-sensitive)
                    const functionMetadata = {
                        targetContract: contractAddress || '',
                        functionName: selectedFunctionObj.name,
                        inputNames: selectedFunctionObj.inputs?.map((input: any) => input.name) || [],
                        inputTypes: selectedFunctionObj.inputs?.map((input: any) => input.type) || [],
                        timestamp: Date.now()
                    };

                    // Try client-side encryption first
                    let securePayload;
                    let useClientEncryption = true;

                    try {
                        // HYBRID APPROACH: Use session-based encryption for network security
                        logger.log('🔐 Attempting session-based encryption...');

                        // CRITICAL FIX: Create payload FIRST, then encrypt the ENTIRE payload
                        const payloadToEncrypt = {
                            id: functionId,
                            name: `Workflow Data ${Date.now()}`,
                            description: 'Session-encrypted workflow function data',
                            category: 'utility',
                            difficulty: 'intermediate',
                            estimatedTime: 5,
                            tags: ['workflow', 'session-encrypted'],
                            created: Date.now(),
                            // Store actual data (will be encrypted as part of whole payload)
                            functionData: data,
                            inputs: params,
                            // Non-sensitive metadata
                            metadata: functionMetadata
                        };

                        // Encrypt the ENTIRE payload as one blob
                        const encryptedPayload = await encryptForSession(payloadToEncrypt);

                        // Create secure payload with encryption metadata
                        securePayload = {
                            id: functionId,
                            name: `Workflow Data ${Date.now()}`,
                            description: 'Session-encrypted workflow function data',
                            category: 'utility',
                            difficulty: 'intermediate',
                            estimatedTime: 5,
                            tags: ['workflow', 'session-encrypted'],
                            created: Date.now(),
                            // CRITICAL: Session encryption flags and data
                            sessionEncrypted: true,
                            sessionId: encryptedPayload.sessionId,
                            encrypted: encryptedPayload.encrypted,
                            iv: encryptedPayload.iv,
                            // Non-sensitive metadata
                            metadata: functionMetadata
                        };

                        logger.log('✅ Session-based encryption successful - entire payload encrypted');

                    } catch (encryptionError) {
                        logger.warn('⚠️ Session-based encryption failed, falling back to server-side encryption:', encryptionError);
                        useClientEncryption = false;

                        // Fallback to server-side encryption (original method)
                        const fallbackData = {
                            functionData: data,
                            targetContract: contractAddress || '',
                            functionName: selectedFunctionObj.name,
                            inputs: params,
                            inputNames: selectedFunctionObj.inputs?.map((input: any) => input.name) || [],
                            inputTypes: selectedFunctionObj.inputs?.map((input: any) => input.type) || [],
                            timestamp: Date.now()
                        };

                        securePayload = {
                            id: functionId,
                            name: `Workflow Data ${Date.now()}`,
                            description: 'Server-encrypted workflow function data',
                            category: 'utility',
                            difficulty: 'intermediate',
                            estimatedTime: 5,
                            tags: ['workflow', 'server-encrypted'],
                            created: Date.now(),
                            functionData: typeof fallbackData === 'string' ? fallbackData : JSON.stringify(fallbackData),
                            templateData: fallbackData
                        };

                        logger.log('✅ Fallback payload prepared for server-side encryption');
                    }

                    // Send payload to backend
                    const storeResult = await backendDataHandler.storeEncodedData(securePayload, 'workflow', functionId);

                    if (storeResult.success) {
                        // CRITICAL FIX: Use the returned ID from backend, not the local functionId
                        const backendId = storeResult.data?.id || functionId;
                        logger.log(`🔐 Function data stored securely with ${useClientEncryption ? 'client' : 'server'} encryption, ID:`, backendId);

                        // Create secure reference for UI display using the FULL backend ID
                        const secureReference = `{{${backendId}}}`;

                        // Set success state and secure reference for UI display
                        setBackendStorageSuccess(true);
                        setWorkflowReference(secureReference);

                        // Emit event for any listeners (workflows, analytics, etc.) with secure reference
                        const functionBuiltEvent = new CustomEvent('functionBuilt', {
                            detail: {
                                functionReference: backendId, // Use backend ID, not local functionId
                                secureReference: secureReference,
                                targetContract: contractAddress || '',
                                functionName: selectedFunctionObj.name,
                                inputCount: params.length,
                                timestamp: Date.now(),
                                encryptionType: useClientEncryption ? 'client' : 'server'
                            }
                        });
                        window.dispatchEvent(functionBuiltEvent);
                        logger.log('📡 Emitted functionBuilt event with secure reference for listeners');

                        // Pass secure reference to parent - NEVER raw data
                        onFunctionBuilt(secureReference);

                    } else {
                        throw new Error(storeResult.error || 'Secure storage failed');
                    }

                } catch (storageError) {
                    logger.error('❌ Secure storage failed:', storageError);
                    // Set failure state for UI display
                    setBackendStorageSuccess(false);
                    setWorkflowReference(null);

                    // Even on failure, we should not pass raw data - this is a critical security principle
                    throw new Error('Function building failed: Secure storage is required but unavailable');
                }

                saveTemplate({
                    contract: "CurrentContractName",
                    functionName: selectedFunctionObj.name,
                    inputs: params,
                });

                logger.log('✅ Function built successfully with backend storage approach');
            } catch (error) {
                logger.error('Error building function:', error);
                setEncodedData(null);
            } finally {
                setIsStoringSecurely(false);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 4, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    {parentFunctionName
                        ? `Build Nested Function for ${parentFunctionName}`
                        : "Build Function"}
                </Typography>
                <Chip
                    label="Backend Storage"
                    color="success"
                    size="small"
                />
            </Box>

            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="template-select-label">Saved Templates</InputLabel>
                <Select
                    labelId="template-select-label"
                    id="template-select"
                    value={selectedTemplateIndex}
                    label="Saved Templates"
                    onChange={(e) => handleTemplateSelect(e.target.value as number)}
                    disabled={isWorkflowStep}
                >
                    {isWorkflowStep ? (
                        <MenuItem value="">
                            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Not Applicable - Workflow Step
                            </Typography>
                        </MenuItem>
                    ) : [
                        <MenuItem key="none" value="">None</MenuItem>,
                        ...templates.map((template, index) => (
                            <MenuItem key={index} value={index}>
                                {template.contract} - {template.functionName}
                            </MenuItem>
                        ))
                    ]}
                </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="function-select-label">Select a Function</InputLabel>
                <Select
                    labelId="function-select-label"
                    id="function-select"
                    value={selectedFunctionObj ? selectedFunctionObj.name : ""}
                    label="Select a Function"
                    onChange={(e) => {
                        const funcName = e.target.value;
                        const funcObj = abi.find(item =>
                            item.type === "function" && item.name === funcName
                        );
                        if (funcObj) {
                            handleFunctionSelect(funcObj);
                        }
                    }}
                >
                    <MenuItem value="">Select a Function</MenuItem>
                    {abi
                        .filter((item) => item.type === "function")
                        .map((func, index) => (
                            <MenuItem key={index} value={func.name}>
                                <Typography sx={{ mr: 1 }}>{func.name}</Typography>
                                {func.stateMutability === 'view' || func.stateMutability === 'pure' ? (
                                    <Chip
                                        label="Read-only"
                                        size="small"
                                        color="primary"
                                    />
                                ) : (
                                    <Chip
                                        label="Write function"
                                        size="small"
                                        color="secondary"
                                    />
                                )}
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>

            {selectedFunctionObj && (
                <Box sx={{ mt: 2 }}>
                    <form onSubmit={(e) => e.preventDefault()}>
                        {selectedFunctionObj.inputs && selectedFunctionObj.inputs.length > 0 ? (
                            selectedFunctionObj.inputs.map((input: any, index: number) => {
                                const isSaltParam = input.name?.toLowerCase().includes('salt') ||
                                    input.name?.toLowerCase() === 'salt' ||
                                    (input.type === 'bytes32' && selectedFunctionObj.name?.toLowerCase().includes('schedule'));

                                const isAddressParam = input.type === 'address';

                                return (
                                    <FormControl fullWidth key={index} sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {isAddressParam ? (
                                                <AddressSelector
                                                    value={localInputs[input.name] || inputs[selectedFunctionObj.name]?.[index] || ""}
                                                    onChange={(value) => handleInputChange(input.name, value)}
                                                    contracts={contracts}
                                                    protocolAddresses={protocolAddresses}
                                                    label={`${input.name || `param${index + 1}`} (${input.type})`}
                                                    placeholder="Select contract or enter address"
                                                    allowManualInput={true}
                                                    helperText={`Select from Memer ecosystem contracts, protocol addresses, or enter custom address`}
                                                />
                                            ) : (
                                                <TextField
                                                    label={`${input.name || `param${index + 1}`} (${input.type})`}
                                                    value={localInputs[input.name] || inputs[selectedFunctionObj.name]?.[index] || ""}
                                                    onChange={(e) => handleInputChange(input.name, e.target.value)}
                                                    onKeyDown={handleKeyDown}
                                                    fullWidth
                                                />
                                            )}

                                            {/* Wei Converter for uint256 fields */}
                                            {input.type.startsWith('uint') && !isAddressParam && !isSaltParam && (
                                                <WeiInputConverter
                                                    value={localInputs[input.name] || inputs[selectedFunctionObj.name]?.[index] || ""}
                                                    onChange={(value) => handleInputChange(input.name, value)}
                                                    label={input.name || `param${index + 1}`}
                                                    tokenSymbol="MEMER"
                                                    tokenDecimals={18}
                                                />
                                            )}

                                            {isSaltParam && (
                                                <Tooltip title="Generate Random Salt">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        type="button"
                                                        sx={{ ml: 1, whiteSpace: 'nowrap', minWidth: 'auto' }}
                                                        onClick={() => {
                                                            const salt = generateRandomSalt();
                                                            handleInputChange(input.name, salt);
                                                        }}
                                                    >
                                                        Generate Salt
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </FormControl>
                                );
                            })
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                This function has no parameters.
                            </Typography>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleBuildFunction}
                            disabled={!selectedFunctionObj || isStoringSecurely}
                            sx={{ mt: 2 }}
                        >
                            {isStoringSecurely ? 'Storing Securely...' : 'Build Function'}
                        </Button>
                    </form>

                    {/* Secure Workflow Reference Display (shown when backend storage succeeds) */}
                    {backendStorageSuccess && workflowReference && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="h6" color="success.main" sx={{ mb: 2 }}>
                                Secure Workflow Reference
                            </Typography>
                            <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1, border: '2px solid', borderColor: 'success.main' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" color="success.contrastText">
                                        {workflowReference}
                                    </Typography>
                                    <Tooltip title="Copy reference to clipboard">
                                        <IconButton
                                            size="small"
                                            onClick={() => navigator.clipboard.writeText(workflowReference)}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography variant="body2" color="success.contrastText">
                                    Function: Secure reference to: {selectedFunctionObj.name}
                                </Typography>
                                <Typography variant="caption" color="success.contrastText" sx={{ mt: 1, display: 'block' }}>
                                    🔒 Actual function data is encrypted and stored securely on the backend. This reference will be resolved automatically when needed.
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Raw Data Display (only shown if backend storage fails) */}
                    {encodedData && !backendStorageSuccess && (
                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="h6" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WarningIcon />
                                    Security Warning
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Secure storage failed. Raw encoded data is displayed below. This poses a security risk if your machine is compromised.
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1, border: '2px solid', borderColor: 'warning.main' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2" color="warning.contrastText">
                                        ⚠️ Raw Encoded Function Data (Unsecured):
                                    </Typography>
                                    <Tooltip title="Copy to clipboard">
                                        <IconButton
                                            size="small"
                                            onClick={() => navigator.clipboard.writeText(encodedData)}
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'monospace',
                                        wordBreak: 'break-all',
                                        bgcolor: 'white',
                                        p: 1,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'warning.main'
                                    }}
                                >
                                    {encodedData}
                                </Typography>
                                <Typography variant="caption" color="warning.contrastText" sx={{ mt: 1, display: 'block' }}>
                                    ⚠️ This raw data could be copied and executed if your system is compromised.
                                    Consider refreshing to retry secure storage.
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Paper>
    );
};

export default FunctionBuilder;
